<template>
  <div>
    <v-app-bar app color="white">
      <v-app-bar-nav-icon @click="closeDrawer" />
      <v-spacer />
      <v-autocomplete
        return-object
        hide-details
        hide-selected
        :items="companies"
        item-text="name"
        item-value="id"
        placeholder="Escolha a empresa"
        outlined
        color="accent"
        :value="selectedCompany"
        @input="selectCompany"
      />
      <v-spacer />
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" app>
      <v-sheet color="primary white--text" class="pa-5">
        <div class="text-center">
          <div
            class="contract-logo"
            v-if="
              selectedCompany
                && selectedCompany.contract
                && selectedCompany.contract.logo_url
            "
          >
            <img
              :src="selectedCompany.contract.logo_url"
              :alt="selectedCompany && selectedCompany.name"
            />
          </div>
          <div class="text-truncate">
            {{
              (selectedCompany && selectedCompany.name) || "Escolha uma empresa"
            }}
          </div>
          <div class="text-truncate">{{ user && user.name }}</div>
          <div class="text-bold text-truncate">{{ user && user.email }}</div>
        </div>
      </v-sheet>

      <v-divider />

      <v-list>
        <v-list-item :to="{ name: 'dashboard' }">
          <v-list-item-icon class="mr-5">
            <v-icon class="accent--text">mdi-view-dashboard</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Dashboard</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isAdmin" :to="{ name: 'companies' }">
          <v-list-item-icon class="mr-5">
            <v-icon class="accent--text">mdi-domain</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Empresas</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isAdmin" :to="{ name: 'users' }">
          <v-list-item-icon class="mr-5">
            <v-icon class="accent--text">mdi-account</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Usuários</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-show="selectedCompany"
          v-if="isAdminOrEditor"
          :to="{ name: 'signers' }"
        >
          <v-list-item-icon class="mr-5">
            <v-icon class="accent--text">mdi-account-group</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Assinantes</v-list-item-title>
        </v-list-item>
        <v-list-group
          no-link
          v-show="selectedCompany"
        >
          <template v-slot:activator>
            <v-list-item-icon class="mr-5">
              <v-icon class="accent--text">mdi-book-open-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Livros societários <PlanUpgradeInfo v-if="!hasModule('livros_societarios') && selectedCompany" /></v-list-item-title>
          </template>
          <v-list-item :disabled="!hasModule('livros_societarios')" :to="{ name: 'books' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-book-open-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Livros</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isSA" :disabled="!hasModule('livros_societarios')" :to="{ name: 'shareholding-structure' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-table</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Quadro acionário</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isSA && isAdmin" :disabled="!hasModule('livros_societarios')" :to="{ name: 'stocks' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-chart-line-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Ações</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isSA && isAdmin" :disabled="!hasModule('livros_societarios')" :to="{ name: 'debentures' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-chart-line</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Debêntures</v-list-item-title>
          </v-list-item>
          <v-list-item :disabled="!hasModule('livros_societarios')" :to="{ name: 'book-models' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-content-copy</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Modelos</v-list-item-title>
          </v-list-item>
        </v-list-group>
        <v-list-item
          v-show="selectedCompany"
          :disabled="!hasModule('gestao_documentos')"
          :to="{ name: 'library' }"
        >
          <v-list-item-icon class="mr-5">
            <v-icon class="accent--text">mdi-folder</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-tooltip top>
              <span>Gestão eletrônica de documentos</span>
              <template v-slot:activator="{ on, attrs }">
                <v-list-item-title v-bind="attrs" v-on="on">
                  G.E.D.
                </v-list-item-title>
              </template>
            </v-tooltip>
          </v-list-item-content>
        </v-list-item>
        <v-list-group
          no-link
          v-show="selectedCompany"
        >
          <template v-slot:activator>
            <v-list-item-icon class="mr-5">
              <v-icon class="accent--text">mdi-briefcase-account</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Gestão societária <PlanUpgradeInfo v-if="!hasModule('gestao_societaria') && selectedCompany" /></v-list-item-title>
          </template>
          <v-list-item :disabled="!hasModule('gestao_societaria')" :to="{ name: 'meetings' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-video-check</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              <v-tooltip top>
                <span>Workflow de Atos e Eventos</span>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item-title v-bind="attrs" v-on="on">
                    Workflow de Atos e Eventos
                  </v-list-item-title>
                </template>
              </v-tooltip>
            </v-list-item-title>
          </v-list-item>
          <v-list-item :disabled="!hasModule('gestao_societaria')" :to="{ name: 'societary-charts' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-sitemap-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              <v-tooltip top>
                <span>Organograma Societário</span>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item-title v-bind="attrs" v-on="on">
                    Organograma Societário
                  </v-list-item-title>
                </template>
              </v-tooltip>
            </v-list-item-title>
          </v-list-item>
          <v-list-item :disabled="!hasModule('gestao_societaria')" :to="{ name: 'organizational-charts' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-sitemap-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              <v-tooltip top>
                <span>Organograma Organizacional</span>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item-title v-bind="attrs" v-on="on">
                    Organograma Organizacional
                  </v-list-item-title>
                </template>
              </v-tooltip>
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isLTDA" :disabled="!hasModule('gestao_societaria')" :to="{ name: 'shareholding-structure-ltda' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-table</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Quadro societário</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isLTDA && isAdmin" :disabled="!hasModule('gestao_societaria')" :to="{ name: 'quotas' }">
            <v-list-item-icon>
              <v-icon class="accent--text">mdi-chart-line-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Quotas</v-list-item-title>
          </v-list-item>
        </v-list-group>
        <v-list-item
          style="color: rgba(0, 0, 0, 0.38);"
          v-show="selectedCompany"
        >
          <v-list-item-icon class="mr-5">
            <v-icon class="accent--text">mdi-chat</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <v-tooltip top>
                <span>ISA - Inteligência Societária Avançada</span>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item-title v-bind="attrs" v-on="on">
                    ISA - Inteligência Societária Avançada
                  </v-list-item-title>
                </template>
              </v-tooltip>
            </v-list-item-title>
            <v-chip x-small color="error" class="em-breve">em breve</v-chip>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :to="{ name: 'profile' }">
          <v-list-item-icon class="mr-5">
            <v-icon class="accent--text">mdi-account-circle</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Perfil</v-list-item-title>
        </v-list-item>

        <v-divider class="my-5" />
        <!-- <v-list-item link :href="openWhatsapp()" target="_blank" rel="noopener noreferrer">
          <v-list-item-icon>
            <v-icon class="accent--text">mdi-whatsapp</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Fale Conosco</v-list-item-title>
          </v-list-item-content>
        </v-list-item> -->
        <v-list-item link @click="logout">
          <v-list-item-icon>
            <v-icon class="accent--text">mdi-close</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Sair</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-main class="grey lighten-4 fill-height pb-16">
      <v-fade-transition hide-on-leave>
        <router-view />
      </v-fade-transition>
    </v-main>
  </div>
</template>
<script>
import PlanUpgradeInfo from '@/components/PlanUpgradeInfo.vue';

export default {
  name: 'Default',
  components: { PlanUpgradeInfo },
  data: () => ({
    drawer: null,
  }),
  computed: {
    user() {
      return this.$store.getters['auth/user'];
    },
    selectedCompany() {
      return this.$store.state.company.selectedCompany;
    },
    isAdminOrEditor() {
      return (
        this.$store.getters['auth/check']('administrator') ||
        this.$store.getters['auth/check']('editor')
      );
    },
    isAdmin() {
      return this.$store.getters['auth/check']('administrator');
    },
    helpers() {
      return [
        ['mdi-whatsapp', 'Fale Conosco', this.openWhatsapp],
        ['mdi-close', 'Sair', this.logout],
      ];
    },
    isSA() {
      return this.$store.getters['company/isSA'];
    },
    isLTDA() {
      return this.$store.getters['company/isLTDA'];
    },
    companies() {
      return this.$store.getters['auth/myCompanies'];
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    // --- COMPATIBILIDADE COM OS NOVOS MÓDULOS E AUTENTICAÇÃO ---
    getStoredCompany() {
      const LS_KEY = 'digital_company';
      try {
        const raw = localStorage.getItem(LS_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
      // JSON inválido -> considera inválido e remove
        localStorage.removeItem(LS_KEY);
        return null;
      }
    },
    hasContractWithModules(company) {
      const contract = company && company.contract;
      return !!(
        contract &&
      (contract.active === 1 || contract.active === true) &&
      Array.isArray(contract.modules) &&
      contract.modules.length > 0
      );
    },
    async initialize() {
      try {
        const LS_KEY = 'digital_company';
        const stored = this.getStoredCompany();
        const isValid = this.hasContractWithModules(stored);

        if (!isValid) {
        // Remove storage inválido e limpa o estado selecionado
          localStorage.removeItem(LS_KEY);
          try {
          // tenta via mutation (como você descreveu)
            this.$store.commit('company/selectCompany', null);
          } catch (e) {
          // fallback: se você usa apenas action namespaced
            this.$store.dispatch('company/resetCompany');
          }
        }
        setTimeout(() => {
          if (window.$chatwoot) {
            const data = {
              email: this.user?.email,
              name: this.user?.name,
              company_name: this.selectedCompany?.name,
              description: this.user?.roles?.map((role) => role.name).join(', '),
            };
            window.$chatwoot.setUser(this.user?.email, data);
            window.$chatwoot.setCustomAttributes({
              id_usuario: this.user?.id,
              permissao: this.user?.roles?.map((role) => role.name).join(', '),
              empresa: this.selectedCompany?.name,
              contrato: this.user?.companies[0]?.contract.title,
            });
          }
        }, 1000);
        window.addEventListener('chatwoot:error', (error) => {
          console.log(`Chatwoot error: ${JSON.stringify(error)}`);
        });
        await this.$store.dispatch('company/index');
      } catch (error) {
        this.$toasted.error(error.message);
      }
    },
    logout() {
      this.$store.dispatch('auth/logout');
      if (window.$chatwoot) {
        window.$chatwoot.reset();
      }
    },
    openWhatsapp() {
      return this.$vuetify.breakpoint.mobile
        ? `https://api.whatsapp.com/send?phone=${process.env.VUE_APP_SUPPORT_PHONE}&text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estou%20com%20um%20problema%20no%20Societário%20Digital`
        : `https://web.whatsapp.com/send?phone=${process.env.VUE_APP_SUPPORT_PHONE}&text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estou%20com%20um%20problema%20no%20Societário%20Digital`;
    },
    selectCompany(company) {
      this.$store.dispatch('company/selectCompany', company);
      if (this.$route.name !== 'dashboard') {
        this.$router
          .push({ name: 'dashboard' })
          .then(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
      }
    },
    hasModule(modulo) {
      return this.$store.getters['auth/hasModule'](modulo);
    },
    closeDrawer() {
      this.drawer = !this.drawer;
    },
  },
};
</script>
<style scoped>
.contract-logo {
  width: auto;
  display: inline-block;
  padding: 5px;
  background-color: white;
  border-radius: 100%;
  max-height: 110px;
}
.contract-logo img {
  max-width: 100%;
  width: auto;
  height: auto;
  max-height: 100px;
}
.v-list-group >>> .em-breve, .v-list-item >>> .em-breve  {
  opacity: 0;
  transition: 0.3s;
  position: absolute;
  top: 2px;
  right: 5px;
  padding-right: 5px;
  padding-left: 5px;
}
.v-list-group:hover >>> .em-breve, .v-list-item:hover >>> .em-breve  {
  opacity: 0.7;
  transition: 0.3s;
}
</style>
